import { Inject, Injectable, Optional } from '@angular/core';
import { Request } from 'express';
import { consoleHelper } from 'lib/helpers/console-helper.class';
import { jsCookieHelper } from 'lib/helpers/js-cookie-helper.class';
import { PlatformService } from 'lib/platform/platform.service';
import { REQUEST } from 'lib/tokens.js';

@Injectable({
	providedIn: 'root'
})
export class RequestService {
	private readonly milisecondsInDay: number = 86400000;

	/**
	 * This is configured in the cloudflare worker
	 */
	private readonly urlHeaderName: string = 'x-gymvio-url';

	constructor(
		private readonly platformService: PlatformService,
		@Optional() @Inject(REQUEST) private request?: Request
	) {}

	getSubdomainFromServer(): string | undefined {
		if (!this.platformService.isServer) {
			return undefined;
		}

		try {
			const url = this.getUrlFromRequest();

			if (!url) {
				return undefined;
			}

			const hostname = url.hostname;
			const parts = hostname.split('.');

			const subDomain = parts[0];

			if (!parts.length) {
				return;
			}

			return subDomain;
		} catch (e) {
			consoleHelper.logToConsole({
				message: `Failed to get subdomain from server`,
				type: 'Error',
				data: e
			});

			return undefined;
		}
	}

	/**
	 *
	 * @returns url from request header - set by cloudflare worker
	 */
	getUrlFromRequest(): URL | undefined {
		if (!this.platformService.isServer) {
			return undefined;
		}

		if (!this.request) {
			consoleHelper.logToConsole({
				type: 'Warning',
				message: `Cannot get current host on serverdue to missing request`
			});
			return undefined;
		}

		const url = this.request.headers[this.urlHeaderName]?.toString()?.toLowerCase();

		if (!url) {
			return undefined;
		}

		try {
			return new URL(url);
		} catch (e) {
			consoleHelper.logToConsole({
				type: 'Warning',
				message: `Failed to parse url '${url}'`
			});
			return undefined;
		}
	}

	getCookieFromRequest(name: string): string | undefined {
		if (!this.platformService.isServer) {
			return undefined;
		}
		if (!this.request) {
			consoleHelper.logToConsole({
				type: 'Warning',
				message: `Missing request to extract cookie value on server`
			});
			return undefined;
		}

		const cookiesHeader = this.request.headers.cookie;
		const parsedCookies = jsCookieHelper.parseHeaderCookiesToObject(cookiesHeader ?? undefined);

		const parsedCookie = parsedCookies[name];

		if (!parsedCookie) {
			return undefined;
		}

		return parsedCookie.trim();
	}

	setCookieInRequest(
		name: string,
		value: string,
		options?: {
			readonly expiresInDays?: number;
		}
	): void {
		if (!this.platformService.isServer) {
			return undefined;
		}

		// set cookie on server
		if (!this.request) {
			consoleHelper.logToConsole({
				type: 'Warning',
				message: `Missing request to set cookie value on server`
			});
			return undefined;
		}

		if (!this.request.res) {
			consoleHelper.logToConsole({
				type: 'Warning',
				message: `Missing request response to set cookie on server`
			});
			return undefined;
		}

		let expires: Date | undefined = undefined;

		if (options?.expiresInDays) {
			expires = new Date(Date.now() + this.milisecondsInDay * options.expiresInDays);
		}

		this.request.res.cookie(name, value, {
			expires: expires
		});
	}
}
